import { DateTime } from 'luxon';
// ---------------------------------------------------------------------------
// Dates
// ---------------------------------------------------------------------------
// All dashboard/case display dates and times render in UTC. The dates we show
// (chargeback event date, defense period end, submitted at) are inherently
// zone-agnostic calendar dates from Adyen, and mixing them with the browser's
// local zone caused deadline-sensitive values (e.g. `2026-06-23T01:00:00Z`) to
// show a different calendar day between the case header and the details cards
// for operators in western timezones.
const formatDate = (iso, pattern) => {
    if (!iso) {
        return '—';
    }
    const parsed = DateTime.fromISO(iso, { zone: 'utc' });
    return parsed.isValid ? parsed.toFormat(pattern) : iso;
};
export const formatUsDate = (iso) => formatDate(iso, 'MM/dd/yyyy');
export const formatUsDateTime = (iso) => formatDate(iso, 'MM/dd/yyyy HH:mm ZZZZ');
export const formatIsoDateTime = (iso) => formatDate(iso, 'yyyy-MM-dd HH:mm ZZZZ');
// ---------------------------------------------------------------------------
// Amounts
// ---------------------------------------------------------------------------
// ISO 4217 zero-decimal currencies: Adyen ships the "amount" as the currency
// unit itself for these, not a hundredth of a unit — so dividing by 100 (as
// we do for USD/EUR/etc.) turns 1,000 JPY into ¥10.
const ZERO_DECIMAL_CURRENCIES = new Set([
    'BIF',
    'CLP',
    'DJF',
    'GNF',
    'ISK',
    'JPY',
    'KMF',
    'KRW',
    'MGA',
    'PYG',
    'RWF',
    'UGX',
    'VND',
    'VUV',
    'XAF',
    'XOF',
    'XPF'
]);
const minorUnitFactor = (currency) => currency && ZERO_DECIMAL_CURRENCIES.has(currency.toUpperCase()) ? 1 : 100;
const majorDecimals = (currency) => minorUnitFactor(currency) === 1 ? 0 : 2;
// Formats an Adyen-style minor-unit amount for display. Returns an em-dash
// when the amount is missing so table cells stay aligned.
export const formatAmount = (amount, currency) => {
    if (amount === undefined) {
        return '—';
    }
    const major = (amount / minorUnitFactor(currency)).toFixed(majorDecimals(currency));
    return currency ? `${major} ${currency}` : major;
};
// ---------------------------------------------------------------------------
// Title case
// ---------------------------------------------------------------------------
// Preserved as-is when they appear as standalone tokens. Add here for any new
// domain acronym that shouldn't be lower-cased.
const ACRONYMS = new Set([
    'ID',
    'PSP',
    'AVS',
    'CVC',
    '3DS',
    'BIN',
    'MIT',
    'CIT',
    'PDF',
    'JSON',
    'URL',
    'API',
    'IP'
]);
const capitalizeWord = (word) => {
    if (word.length === 0) {
        return word;
    }
    const upper = word.toUpperCase();
    if (ACRONYMS.has(upper)) {
        return upper;
    }
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
};
// Split camelCase / PascalCase / SCREAMING_SNAKE / kebab-case / space-separated
// strings into Title-Cased words. Numeric tokens pass through unchanged; known
// acronyms are preserved.
export const titleCase = (input) => {
    if (input === undefined || input === null || input === '') {
        return '';
    }
    const str = typeof input === 'number' ? input.toString() : input;
    // Split camelCase at lower-to-upper boundaries but keep digit-followed-by-
    // uppercase together (e.g. `3DS`) so acronyms with leading digits survive.
    const spaced = str
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2');
    const words = spaced.split(/[\s_\-]+/).filter(w => w.length > 0);
    return words.map(capitalizeWord).join(' ');
};
